import '@/modules/utility/ds-wms/styles/wmsTable.css';
import { useState, useEffect, useRef, useCallback } from 'react';
import { dashboardApi } from '../../services/wmsService';
import type { TenderDetails, TenderCall, TenderDocument } from '../../lib/types';
import { todayYmd } from '../../lib/dateUtils';
import { ListSkeleton } from '@/components/base/Skeleton';
import { WMS_POPUP_HEADER, WMS_POPUP_ICON, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_CLOSE } from '../common/wmsPopupHeader';
import ContractorLink from '../ContractorLink';

interface TenderProfileProps {
  isOpen: boolean;
  onClose: () => void;
  workId: number;
  workName?: string;
  workCode?: string;
}

const DOCUMENT_CATEGORIES = [
  { code: 'TENDER_DOC', label: 'Tender Document', icon: 'ri-file-text-line' },
  { code: 'CORRIGENDUM', label: 'Corrigendum', icon: 'ri-file-edit-line' },
  { code: 'PREBID_ATTENDEES', label: 'Pre-bid Meeting Attendees', icon: 'ri-group-line' },
  { code: 'PREBID_QUERIES', label: 'Pre-bid Queries & Responses', icon: 'ri-question-answer-line' },
  { code: 'TENDER_EVAL', label: 'Tender Evaluation Report', icon: 'ri-file-chart-line' },
  { code: 'LOA', label: 'Letter of Acceptance', icon: 'ri-mail-check-line' },
  { code: 'EMD', label: 'EMD Document', icon: 'ri-secure-payment-line' },
  { code: 'BANK_GUARANTEE', label: 'Bank Guarantee', icon: 'ri-bank-line' },
  { code: 'WORK_ORDER', label: 'Work Order', icon: 'ri-file-list-3-line' },
  { code: 'AGREEMENT', label: 'Agreement', icon: 'ri-file-shield-2-line' },
  { code: 'SUPP_AGREEMENT', label: 'Supplementary Agreement', icon: 'ri-file-add-line' },
  { code: 'EXT_AGREEMENT', label: 'Extension Agreement', icon: 'ri-time-line' },
];

export default function TenderProfile({ isOpen, onClose, workId, workName, workCode }: TenderProfileProps) {
  const [activeTab, setActiveTab] = useState<'calls' | 'documents' | 'agreement'>('calls');
  const [activeCallIndex, setActiveCallIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tenderDetails, setTenderDetails] = useState<TenderDetails | null>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  const updateIndicator = useCallback(() => {
    if (!tabsRef.current) return;
    const activeButton = tabsRef.current.querySelector(`[data-tab="${activeTab}"]`) as HTMLButtonElement;
    if (activeButton) {
      const containerRect = tabsRef.current.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      setIndicatorStyle({
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width,
      });
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(updateIndicator, 50);
    window.addEventListener('resize', updateIndicator);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateIndicator);
    };
  }, [updateIndicator, isOpen]);

  useEffect(() => {
    if (!isOpen || !workId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await dashboardApi.getTenderDetails(workId);
        setTenderDetails(data);
        setActiveCallIndex(0);
      } catch (error) {
        console.error('Failed to fetch tender details:', error);
        setTenderDetails(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    setActiveTab('calls');
  }, [isOpen, workId]);

  if (!isOpen) return null;

  const formatCurrency = (amount: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    try {
      return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return date; }
  };

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('award') || s.includes('complet')) return 'bg-green-100 text-green-700';
    if (s.includes('open') || s.includes('active') || s.includes('publish')) return 'bg-blue-100 text-blue-700';
    if (s.includes('cancel') || s.includes('fail')) return 'bg-red-100 text-red-700';
    if (s.includes('evaluat')) return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  };

  const tabs = [
    { id: 'calls' as const, label: 'Tender Calls', icon: 'ri-auction-line' },
    { id: 'documents' as const, label: 'Documents', icon: 'ri-folder-3-line' },
    { id: 'agreement' as const, label: 'Agreement Details', icon: 'ri-file-shield-2-line' },
  ];

  const tenderCalls = tenderDetails?.tenderCalls || [];
  const documents = tenderDetails?.documents || [];
  const currentCall = tenderCalls[activeCallIndex] || null;

  const handleExportCSV = () => {
    if (!tenderDetails) return;
    const headers = ['Field', 'Value'];
    const rows: string[][] = [
      ['Work Code', tenderDetails.workCode],
      ['Work Name', tenderDetails.workName],
      ['Contractor', tenderDetails.contractorName],
      ['Contract Value', String(tenderDetails.contractValue)],
      ['Agreement Date', tenderDetails.agreementDate],
      ['', ''],
    ];
    tenderCalls.forEach((call) => {
      rows.push(['--- Call ' + call.callNumber + ' ---', '']);
      rows.push(['Tender Code', call.tenderCode]);
      rows.push(['Status', call.status]);
      rows.push(['NIT Date', call.nitDate]);
      rows.push(['Bid End Date', call.bidEndDate]);
      rows.push(['Award Date', call.awardDate]);
      rows.push(['Estimated Amount', String(call.estimatedAmount)]);
      rows.push(['Contract Value', String(call.contractValue)]);
      rows.push(['', '']);
    });
    const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Tender_${tenderDetails.workCode || workId}_${todayYmd()}.csv`;
    link.click();
  };

  const renderDateRow = (label: string, value: string) => (
    <div className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-purple-50/50">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`text-sm font-medium ${value && value !== '-' ? 'text-gray-900' : 'text-gray-400'}`}>
        {formatDate(value)}
      </span>
    </div>
  );

  const renderTenderCallsTab = () => {
    if (tenderCalls.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <i className="ri-auction-line text-5xl mb-3"></i>
          <p className="text-lg font-medium">No Tender Records Found</p>
          <p className="text-sm mt-1">No formal tender data exists for this work yet.</p>
        </div>
      );
    }

    return (
      <div>
        {/* Call Sub-tabs */}
        {tenderCalls.length > 1 && (
          <div className="flex gap-2 mb-4">
            {tenderCalls.map((call, idx) => (
              <button
                key={call.tenderId}
                onClick={() => setActiveCallIndex(idx)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeCallIndex === idx
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {call.callNumber ? `${getOrdinal(call.callNumber)} Call` : `Call ${idx + 1}`}
              </button>
            ))}
          </div>
        )}

        {currentCall && (
          <div className="space-y-4">
            {/* Header Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <i className="ri-auction-line text-purple-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {currentCall.callNumber ? `${getOrdinal(currentCall.callNumber)} Call` : 'Tender Call'}
                    </h4>
                    <p className="text-sm text-gray-500">{currentCall.tenderCode || 'No Code'}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(currentCall.status)}`}>
                  {currentCall.status || 'N/A'}
                </span>
              </div>
              {currentCall.tenderName && (
                <p className="text-sm text-gray-600 mt-1">{currentCall.tenderName}</p>
              )}
              <div className="flex gap-4 mt-3 text-xs text-gray-500">
                {currentCall.tenderType && <span><i className="ri-price-tag-3-line mr-1"></i>{currentCall.tenderType}</span>}
                {currentCall.evaluationMethod && <span><i className="ri-scales-line mr-1"></i>{currentCall.evaluationMethod}</span>}
              </div>
            </div>

            {/* Key Dates */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-calendar-event-line text-purple-500"></i>
                Key Dates
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5">
                <div className="divide-y divide-gray-100">
                  {renderDateRow('NIT Date', currentCall.nitDate)}
                  {renderDateRow('Tender Publish Date', currentCall.publishDate)}
                  {renderDateRow('Pre-bid Meeting Date', currentCall.preBidDate)}
                  {renderDateRow('Bid Start Date', currentCall.bidStartDate)}
                  {renderDateRow('Tender Closing Date', currentCall.bidEndDate)}
                </div>
                <div className="divide-y divide-gray-100">
                  {renderDateRow('Technical Open Date', currentCall.technicalOpenDate)}
                  {renderDateRow('Financial Open Date', currentCall.financialOpenDate)}
                  {renderDateRow('Award Date', currentCall.awardDate)}
                  {renderDateRow('LoA Date', currentCall.loaDate)}
                  {renderDateRow('Work Order Date', currentCall.workOrderDate)}
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                  <div className="divide-y divide-gray-100">
                    {renderDateRow('Agreement Date', currentCall.agreementDate)}
                  </div>
                  <div className="divide-y divide-gray-100">
                    {renderDateRow('Agreement Start', currentCall.agreementStartDate)}
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-money-rupee-circle-line text-purple-500"></i>
                Financial Summary
              </h5>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-blue-700">{formatCurrency(currentCall.estimatedAmount)}</p>
                  <span className="text-xs text-blue-600">Estimated Amount</span>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-amber-700">{formatCurrency(currentCall.emdAmount)}</p>
                  <span className="text-xs text-amber-600">EMD Amount</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-gray-700">{formatCurrency(currentCall.tenderFee)}</p>
                  <span className="text-xs text-gray-600">Tender Fee</span>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-green-700">{formatCurrency(currentCall.contractValue)}</p>
                  <span className="text-xs text-green-600">Contract Value</span>
                </div>
              </div>
            </div>

            {/* Bidders Table */}
            {currentCall.bidders && currentCall.bidders.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-team-line text-purple-500"></i>
                  Bidders ({currentCall.bidders.length})
                </h5>
                <div className="overflow-x-auto">
                  <table data-wms-table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b-2 border-gray-300">
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bidder Name</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Bid Amount</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">EMD Status</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Tech Score</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentCall.bidders.map((bidder, idx) => (
                        <tr key={bidder.bidderId} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b border-gray-200`}>
                          <td className="px-3 py-2 text-sm text-center">
                            {bidder.rank || idx + 1}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 font-medium">
                            <ContractorLink name={bidder.contractorName} fallback="Unknown" />
                          </td>
                          <td className="px-3 py-2 text-sm text-right font-semibold text-gray-900">
                            {formatCurrency(bidder.bidAmount)}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="text-xs">{bidder.emdStatus || '-'}</span>
                          </td>
                          <td className="px-3 py-2 text-sm text-center">
                            {bidder.technicalScore || '-'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {bidder.isL1 && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">L1</span>
                            )}
                            {bidder.isAwarded && (
                              <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">Awarded</span>
                            )}
                            {!bidder.isL1 && !bidder.isAwarded && <span className="text-xs text-gray-400">-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDocumentsTab = () => {
    const docsByCategory: Record<string, TenderDocument[]> = {};
    documents.forEach(doc => {
      const cat = doc.category || 'OTHER';
      if (!docsByCategory[cat]) docsByCategory[cat] = [];
      docsByCategory[cat].push(doc);
    });

    return (
      <div className="space-y-4">
        {DOCUMENT_CATEGORIES.map(cat => {
          const docs = docsByCategory[cat.code] || [];
          return (
            <div key={cat.code} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <i className={`${cat.icon} text-purple-500`}></i>
                  <span className="text-sm font-medium text-gray-700">{cat.label}</span>
                  {docs.length > 0 && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{docs.length}</span>
                  )}
                </div>
                <button
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                  title={`Upload ${cat.label}`}
                >
                  <i className="ri-upload-2-line"></i>
                  Upload
                </button>
              </div>
              {docs.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {docs.map(doc => (
                    <div key={doc.documentId} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
                      <div className="flex items-center gap-3 min-w-0">
                        <i className="ri-file-line text-gray-400"></i>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{doc.documentName}</p>
                          <p className="text-xs text-gray-500">
                            {doc.fileType && <span className="uppercase">{doc.fileType}</span>}
                            {doc.fileSize ? ` • ${(doc.fileSize / 1024).toFixed(0)} KB` : ''}
                            {doc.documentDate && ` • ${formatDate(doc.documentDate)}`}
                          </p>
                          {cat.code === 'BANK_GUARANTEE' && doc.bgBank && (
                            <p className="text-xs text-blue-600 mt-0.5">
                              Bank: {doc.bgBank} | Value: {doc.bgValue ? formatCurrency(doc.bgValue) : '-'}
                              {doc.bgStartDate && ` | ${formatDate(doc.bgStartDate)} - ${formatDate(doc.bgEndDate || '')}`}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                        <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="View">
                          <i className="ri-eye-line"></i>
                        </button>
                        <button className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors" title="Download">
                          <i className="ri-download-line"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-4 text-center">
                  <p className="text-xs text-gray-400">No documents uploaded</p>
                  <div className="mt-2 border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:bg-purple-50/30 transition-colors cursor-pointer">
                    <i className="ri-upload-cloud-2-line text-2xl text-gray-300"></i>
                    <p className="text-xs text-gray-400 mt-1">Drag & drop files here or click to browse</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderAgreementTab = () => {
    const details = tenderDetails;
    if (!details) return null;

    return (
      <div className="space-y-4">
        {/* Agreement Summary Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <i className="ri-file-shield-2-line text-purple-500"></i>
            Agreement Summary
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Agreement Number</span>
                <span className="font-medium text-gray-900">{details.agreementNumber || '-'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Agreement Date</span>
                <span className="font-medium text-gray-900">{formatDate(details.agreementDate)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Start Date</span>
                <span className="font-medium text-gray-900">{formatDate(details.agreementStartDate)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">End Date</span>
                <span className="font-medium text-gray-900">{formatDate(details.agreementEndDate)}</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Duration</span>
                <span className="font-medium text-gray-900">
                  {details.agreementStartDate && details.agreementEndDate
                    ? `${Math.ceil((new Date(details.agreementEndDate).getTime() - new Date(details.agreementStartDate).getTime()) / (1000 * 60 * 60 * 24))} days`
                    : '-'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Contract Value</span>
                <span className="font-semibold text-green-700">{formatCurrency(details.contractValue)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Contractor</span>
                <span className="font-medium text-gray-900"><ContractorLink name={details.contractorName} /></span>
              </div>
            </div>
          </div>
        </div>

        {/* Bank Guarantee Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <i className="ri-bank-line text-purple-500"></i>
            Bank Guarantee Details
          </h5>
          {(() => {
            const bgDocs = documents.filter(d => d.category === 'BANK_GUARANTEE');
            if (bgDocs.length === 0) {
              return (
                <div className="text-center py-6 text-gray-400">
                  <i className="ri-bank-line text-3xl mb-2 block"></i>
                  <p className="text-sm">No bank guarantee records found</p>
                </div>
              );
            }
            return (
              <div className="overflow-x-auto">
                <table data-wms-table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">BG Bank</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">BG Value</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Start Date</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">End Date</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bgDocs.map((doc, idx) => {
                      const isExpired = doc.bgEndDate ? new Date(doc.bgEndDate) < new Date() : false;
                      return (
                        <tr key={doc.documentId} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b border-gray-100`}>
                          <td className="px-3 py-2 text-sm text-gray-900">{doc.documentName}</td>
                          <td className="px-3 py-2 text-sm text-gray-600">{doc.bgBank || '-'}</td>
                          <td className="px-3 py-2 text-sm text-right font-semibold">{doc.bgValue ? formatCurrency(doc.bgValue) : '-'}</td>
                          <td className="px-3 py-2 text-sm text-center">{formatDate(doc.bgStartDate || '')}</td>
                          <td className="px-3 py-2 text-sm text-center">{formatDate(doc.bgEndDate || '')}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isExpired ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                              {isExpired ? 'Expired' : 'Active'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>

        {/* Supplementary / Extension Agreements */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <i className="ri-file-add-line text-purple-500"></i>
            Supplementary & Extension Agreements
          </h5>
          {(() => {
            const suppDocs = documents.filter(d => d.category === 'SUPP_AGREEMENT' || d.category === 'EXT_AGREEMENT');
            if (suppDocs.length === 0) {
              return (
                <div className="text-center py-6 text-gray-400">
                  <i className="ri-file-add-line text-3xl mb-2 block"></i>
                  <p className="text-sm">No supplementary or extension agreements found</p>
                </div>
              );
            }
            return (
              <div className="space-y-2">
                {suppDocs.map(doc => (
                  <div key={doc.documentId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <i className={`${doc.category === 'SUPP_AGREEMENT' ? 'ri-file-add-line text-blue-500' : 'ri-time-line text-amber-500'}`}></i>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.documentName}</p>
                        <p className="text-xs text-gray-500">{doc.category === 'SUPP_AGREEMENT' ? 'Supplementary' : 'Extension'} • {formatDate(doc.documentDate)}</p>
                      </div>
                    </div>
                    <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="View">
                      <i className="ri-eye-line"></i>
                    </button>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] ${isFullscreen ? 'p-0' : 'p-4'}`}>
      <div className={`bg-gray-100 shadow-2xl overflow-hidden flex flex-col ${isFullscreen ? 'w-full h-full rounded-none' : 'rounded-xl w-full max-w-6xl max-h-[90vh]'}`}>
        {/* Header */}
        <div className={WMS_POPUP_HEADER}>
            <div className="flex items-center gap-2 min-w-0">
              <div className={WMS_POPUP_ICON}>
                <i className="ri-auction-fill"></i>
              </div>
              <div className="min-w-0">
                <h2 className={WMS_POPUP_TITLE}>Tender Profile</h2>
                <p className={WMS_POPUP_SUBTITLE}>
                  {workCode || tenderDetails?.workCode || ''} {workName || tenderDetails?.workName ? `- ${(workName || tenderDetails?.workName || '').substring(0, 60)}${(workName || tenderDetails?.workName || '').length > 60 ? '...' : ''}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className={WMS_POPUP_CLOSE}
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              >
                <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`}></i>
              </button>
              <button
                onClick={handleExportCSV}
                className={WMS_POPUP_CLOSE}
                title="Export CSV"
              >
                <i className="ri-file-excel-2-line text-base"></i>
              </button>
              <button
                onClick={onClose}
                className={WMS_POPUP_CLOSE}
                title="Close"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
        </div>

        {/* Summary Cards */}
        <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0 bg-white">
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-purple-50 rounded-lg p-3 text-center border border-purple-200">
              <p className="text-2xl font-bold text-purple-700">{tenderCalls.length}</p>
              <span className="text-xs text-purple-600">Tender Calls</span>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
              <p className="text-2xl font-bold text-blue-700">{documents.length}</p>
              <span className="text-xs text-blue-600">Documents</span>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
              <p className="text-lg font-bold text-green-700">{formatCurrency(tenderDetails?.contractValue || 0)}</p>
              <span className="text-xs text-green-600">Contract Value</span>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center border border-amber-200">
              <p className="text-sm font-bold text-amber-700 truncate"><ContractorLink name={tenderDetails?.contractorName} /></p>
              <span className="text-xs text-amber-600">Contractor</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b border-gray-200 px-4 flex-shrink-0">
          <div ref={tabsRef} className="relative flex gap-0">
            <div
              className="absolute bottom-0 h-0.5 bg-purple-600 transition-all duration-300 ease-in-out"
              style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
            />
            {tabs.map(tab => (
              <button
                key={tab.id}
                data-tab={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <ListSkeleton />
          ) : (
            <>
              {activeTab === 'calls' && renderTenderCallsTab()}
              {activeTab === 'documents' && renderDocumentsTab()}
              {activeTab === 'agreement' && renderAgreementTab()}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-3 flex justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 hover:border-green-300 transition-all"
            >
              <i className="ri-file-excel-2-line text-lg"></i>
              <span>Excel</span>
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
